// Environment detection for MintKitchen
// Designed to work WITH Amplify Gen 2's native environment management

/**
 * Get the current environment
 * Prioritizes Amplify's official AMPLIFY_ENVIRONMENT (configured in console) over fallbacks
 */
export const getEnvironment = () => {
  // 1. First priority: Amplify's official AMPLIFY_ENVIRONMENT variable (available during build)
  const amplifyEnv = process.env.REACT_APP_AMPLIFY_ENVIRONMENT || process.env.AMPLIFY_ENVIRONMENT;
  
  // 2. Check for NEXT_PUBLIC_RUNTIME_ENV (your custom variable)
  const nextPublicEnv = process.env.REACT_APP_NEXT_PUBLIC_RUNTIME_ENV || process.env.NEXT_PUBLIC_RUNTIME_ENV;
  
  // 3. Fallback to our custom REACT_APP_ENVIRONMENT 
  const reactEnv = process.env.REACT_APP_ENVIRONMENT;
  
  // Debug logging for environment detection (reduced verbosity)
  if (process.env.NODE_ENV === 'development' || !amplifyEnv) {
    console.log(`🔍 Environment detection:`, 
      `REACT_APP_AMPLIFY_ENVIRONMENT=${process.env.REACT_APP_AMPLIFY_ENVIRONMENT || 'undefined'}, ` +
      `REACT_APP_ENVIRONMENT=${reactEnv || 'undefined'}, ` +
      `hostname=${typeof window !== 'undefined' ? window.location.hostname : 'SSR'}`
    );
  }
  
  // Check all possible environment sources
  if (amplifyEnv) {
    return amplifyEnv;
  }
  
  if (nextPublicEnv) {
    return nextPublicEnv;
  }
  
  if (reactEnv) {
    return reactEnv;
  }
  
  // 2. Fallback for hostname-based detection (when REACT_APP_ENVIRONMENT not set)
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  
  if (hostname === 'localhost' || hostname.startsWith('127.0.0.1') || hostname.startsWith('192.168.')) {
    return 'sandbox';  // Local development should use sandbox
  }
  
  // 3. Amplify hosting pattern analysis (more robust detection)
  if (hostname.includes('.amplifyapp.com')) {
    // Check for branch-specific patterns in the subdomain
    if (hostname.includes('staging') || hostname.startsWith('staging.')) {
      return 'staging';
    }
    
    if (hostname.includes('main') || hostname.startsWith('main.')) {
      return 'main';
    }
    
    if (hostname.includes('develop') || hostname.startsWith('develop.')) {
      return 'develop';
    }
    
    // For unknown amplify patterns, analyze the full hostname structure
    console.log(`🔍 Analyzing unknown amplify hostname structure:`, hostname);
    
    // The hostname might be like: branch-name.d2glls1aygkcd.amplifyapp.com
    // or: d2glls1aygkcd-branch.amplifyapp.com
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      const subdomain = parts[0];
      console.log(`🔍 Subdomain analysis:`, subdomain);
      
      if (subdomain.includes('staging')) {
        console.log(`🧪 Staging in subdomain: staging`);
        return 'staging';
      }
      if (subdomain.includes('main') || subdomain.includes('prod')) {
        console.log(`🌐 Production in subdomain: main`);
        return 'main';
      }
      if (subdomain.includes('develop') || subdomain.includes('dev')) {
        console.log(`🔧 Development in subdomain: develop`);
        return 'develop';
      }
    }
    
    // Final amplify fallback - should rarely be reached
    console.warn(`⚠️ Unknown Amplify pattern, cannot determine environment from hostname:`, hostname);
    console.warn(`🔍 Falling back to develop for safety`);
    return 'develop';
  }
  
  // 4. Custom domain detection
  if (hostname.includes('mintkitchen.com')) {
    if (hostname.includes('staging')) {
      console.log(`🧪 Staging custom domain: staging`);
      return 'staging';
    }
    console.log(`🌐 Production custom domain: main`);
    return 'main';
  }
  
  // 5. Final fallback (should rarely be used with proper Amplify setup)
  console.warn(`⚠️ No environment could be determined, defaulting to develop`);
  console.warn(`🔍 Debug info:`, { amplifyEnv, hostname, nodeEnv: process.env.NODE_ENV });
  return 'develop';
};

/**
 * Check if we're in a specific environment
 */
export const isDevelopment = () => getEnvironment() === 'develop';
export const isStaging = () => getEnvironment() === 'staging';
export const isProduction = () => getEnvironment() === 'main';
export const isSandbox = () => getEnvironment() === 'sandbox';

/**
 * Get frontend-specific configuration
 * Only for features NOT handled by Amplify's environment system
 */
export const getFrontendConfig = () => {
  const env = getEnvironment();
  
  return {
    // UI/UX settings that vary by environment
    showDebugInfo: env === 'sandbox' || env === 'develop',
    enableAnalytics: env === 'staging' || env === 'main',
    showEnvironmentBanner: env !== 'main',
    
    // Matomo Analytics Configuration
    matomo: {
      enabled: env === 'main', // Only track production/main branch
      url: 'https://mintkitchen.matomo.cloud',
      siteId: '1', // Only production Site ID needed
    },
    
    // Password requirements (for admin UI feedback)
    passwordRequirements: (() => {
      const requirements: Record<string, { minLength: number; requireComplexity: boolean }> = {
        sandbox: { minLength: 6, requireComplexity: false },
        develop: { minLength: 8, requireComplexity: true },
        staging: { minLength: 12, requireComplexity: true },
        main: { minLength: 12, requireComplexity: true }
      };
      return requirements[env] || { minLength: 8, requireComplexity: true };
    })(),
    
    // Feature flags
    features: {
      adminDebugMode: env === 'sandbox' || env === 'develop',
      advancedCourseManagement: env !== 'sandbox',
      paymentIntegration: env === 'staging' || env === 'main'
    }
  };
};

/**
 * Get the current environment for display purposes
 */
export const getEnvironmentDisplay = () => {
  const env = getEnvironment();
  
  const displayNames: Record<string, string> = {
    sandbox: 'Development (Sandbox)',
    develop: 'Development', 
    staging: 'Staging',
    main: 'Production'
  };
  
  return displayNames[env] || env;
};

// Log current environment for debugging
console.log(`🌍 Environment: ${getEnvironment()} (${getEnvironmentDisplay()})`);

/**
 * IMPORTANT: Data filtering by environment should NOT be needed!
 * 
 * Amplify Gen 2 automatically isolates data per environment:
 * - Each branch deployment gets its own backend
 * - Each backend has its own database tables
 * - amplify_outputs.json points to the correct environment's API
 * 
 * If you find yourself filtering GraphQL queries by environment,
 * you're probably not leveraging Amplify's environment isolation properly.
 */